// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../../primitives/I18n.res.js";
import * as React from "react";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as Accounting__Utils from "@wino/accounting/src/Accounting__Utils.res.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as CanarySummaryAmounts from "../../resources/data-visualizations/CanarySummaryAmounts.res.js";

function formatCurrency(value) {
  return I18n.currencyFormat(undefined, "EUR", 3, 3, value);
}

function formatPercentage(value) {
  let rate = value / 100;
  return I18n.percentFormat(undefined, undefined, 2, rate);
}

function feeKindToLabel(kind) {
  switch (kind) {
    case "Transport" :
      return I18n.t("Transport");
    case "Taxes" :
      return I18n.t("Taxes");
    case "Other" :
      return I18n.t("Other");
  }
}

function discountFromCart(discount) {
  return {
    name: discount.name,
    value: discount.value,
    amount: Stdlib_Option.getOr(Stdlib_Option.map(discount.amount, Accounting__Utils.bigToFloat), 0),
    isPercentage: discount.kind === "Percent"
  };
}

function feeFromCart(fee) {
  return {
    kind: fee.kind,
    amount: fee.amount.toNumber()
  };
}

function taxFromCart(tax) {
  return {
    rate: tax.rate,
    taxAmount: Stdlib_Option.getOr(Stdlib_Option.map(tax.amount, Accounting__Utils.bigToFloat), 0)
  };
}

function OrderCartSummaryAmounts(props) {
  let cart = props.cart;
  let totalAmountExcludingGlobalDiscounts = Stdlib_Option.getOr(Stdlib_Option.map(cart.totalAmountExcludingGlobalDiscounts, Accounting__Utils.bigToFloat), 0);
  let totalAmountExcludingTaxes = Stdlib_Option.getOr(Stdlib_Option.map(cart.totalAmountExcludingTaxes, Accounting__Utils.bigToFloat), 0);
  let totalAmountOfGoods = Stdlib_Option.getOr(Stdlib_Option.map(cart.totalAmountOfGoods, Accounting__Utils.bigToFloat), 0);
  let totalAmountIncludingTaxes = Stdlib_Option.getOr(Stdlib_Option.map(cart.totalAmountIncludingTaxes, Accounting__Utils.bigToFloat), 0);
  let discounts = cart.discounts.map(discountFromCart);
  let fees = Stdlib_Option.getOr(cart.fees, []).map(feeFromCart);
  let taxes = Stdlib_Option.getOr(cart.taxes, []).filter(tax => {
    let amount = tax.amount;
    if (amount !== undefined) {
      return Accounting__Utils.bigToFloat(Primitive_option.valFromOption(amount)) > 0;
    } else {
      return false;
    }
  }).map(taxFromCart);
  let hasDiscounts = discounts.length !== 0;
  let hasFees = fees.length !== 0;
  return JsxRuntime.jsxs(CanarySummaryAmounts.make, {
    children: [
      hasDiscounts ? JsxRuntime.jsxs(JsxRuntime.Fragment, {
          children: [
            JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
              label: I18n.t("Subtotal excl. VAT"),
              value: totalAmountExcludingGlobalDiscounts
            }),
            discounts.map(discount => {
              let formattedValue = discount.isPercentage ? formatPercentage(discount.value) : formatCurrency(discount.value);
              return JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
                label: I18n.template(I18n.t("Discount {{amount}}"), {
                  amount: formattedValue
                }, undefined),
                value: discount.amount
              }, discount.name);
            }),
            JsxRuntime.jsx(CanarySummaryAmounts.Gap.make, {})
          ]
        }) : null,
      JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
        variant: cart.taxesFree ? "strong" : "default",
        label: I18n.t("Total amount excl. VAT"),
        value: totalAmountExcludingTaxes
      }),
      JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
        variant: "subtle",
        label: I18n.t("Goods total excl. VAT"),
        value: totalAmountOfGoods
      }),
      hasFees ? fees.map((fee, index) => JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
          variant: "subtle",
          label: I18n.template(I18n.t("Total {{feeKind}}"), {
            feeKind: feeKindToLabel(fee.kind)
          }, undefined),
          value: fee.amount
        }, index.toString())) : null,
      JsxRuntime.jsx(CanarySummaryAmounts.Gap.make, {}),
      taxes.map(tax => {
        let rateLabel = I18n.t("VAT") + " " + formatPercentage(tax.rate);
        return JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
          label: rateLabel,
          value: tax.taxAmount
        }, tax.rate.toString());
      }),
      cart.taxesFree ? JsxRuntime.jsx(CanarySummaryAmounts.Caption.make, {
          children: I18n.template(I18n.t("{{amount}} of deductible VAT ({{percent}})"), {
            amount: formatCurrency(0.000),
            percent: formatPercentage(0.00)
          }, undefined)
        }) : JsxRuntime.jsx(CanarySummaryAmounts.Line.make, {
          variant: "strong",
          label: I18n.t("Total amount incl. VAT"),
          value: totalAmountIncludingTaxes
        })
    ],
    fractionDigits: 3
  });
}

let make = React.memo(OrderCartSummaryAmounts, (oldProps, newProps) => oldProps.cart === newProps.cart);

let bigToFloat = Accounting__Utils.bigToFloat;

export {
  bigToFloat,
  formatCurrency,
  formatPercentage,
  feeKindToLabel,
  discountFromCart,
  feeFromCart,
  taxFromCart,
  make,
}
/* make Not a pure module */
